import { openaiService } from './openaiService';
import { Task, Lead, Company, Opportunity } from '../types';
import { assistantFallbackService } from './assistantFallbackService';

export interface UserContext {
  userId: string;
  orgId: string;
  userName?: string;
  role?: 'admin' | 'manager' | 'sales_rep' | string;
}

export interface AIResponse {
  response: string;
  data?: any;
  action?: string;
  cards?: InteractiveCard[];
}

export interface InteractiveCard {
  id: string;
  type: 'task' | 'contact' | 'company' | 'opportunity';
  title: string;
  subtitle?: string;
  details: Record<string, any>;
  actions: CardAction[];
  priority?: 'high' | 'medium' | 'low';
  status?: string;
  deepLink: DeepLink;
}

export interface CardAction {
  label: string;
  action: 'view' | 'edit' | 'complete' | 'delete' | 'navigate';
  primary?: boolean;
}

export interface DeepLink {
  itemType: 'task' | 'contact' | 'opportunity' | 'company';
  itemId: string;
  action: 'view' | 'edit';
  label: string;
}

function isMutationIntent(query: string): boolean {
  const q = query.toLowerCase();
  return /\b(create|add|make|new|set up|remind|erinnere|erstelle|erstellen|anlegen|hinzufügen|neu)\b/.test(q);
}

export const aiAssistantService = {
  /**
   * Process natural language query with full CRM context
   */
  async processQuery(
    query: string,
    context: UserContext,
    crmData?: {
      tasks?: Task[];
      contacts?: Lead[];
      opportunities?: Opportunity[];
      companies?: Company[];
    }
  ): Promise<AIResponse> {
    const localAnswer = assistantFallbackService.answer(query, crmData);
    if (!isMutationIntent(query) && localAnswer.action !== 'assistant_general') {
      return localAnswer;
    }

    const hasOpenAIKey = Boolean(import.meta.env.VITE_OPENAI_API_KEY);

    if (!hasOpenAIKey) {
      return localAnswer;
    }

    try {
      // Call OpenAI service with enhanced context
      const result = await openaiService.processQuery(query, {
        userId: context.userId,
        orgId: context.orgId,
        userName: context.userName,
        role: context.role
      });

      // If we have data results, generate interactive cards
      if (result.data && Array.isArray(result.data)) {
        const itemType = this.detectItemType(result.action || '', result.data);
        const cards = this.generateResultCards(result.data, itemType);
        
        return {
          ...result,
          cards
        };
      }

      return result;
    } catch (error) {
      console.error('AI Assistant query error:', error);
      return localAnswer;
    }
  },

  /**
   * Detect the type of items returned based on action and data structure
   */
  detectItemType(action: string, data: any[]): 'task' | 'contact' | 'company' | 'opportunity' {
    if (action.includes('task') || action.includes('search_tasks')) return 'task';
    if (action.includes('contact') || action.includes('search_contacts')) return 'contact';
    if (action.includes('company') || action.includes('search_companies')) return 'company';
    if (action.includes('opportunity') || action.includes('search_opportunities')) return 'opportunity';
    
    // Fallback: detect by data structure
    if (data.length > 0) {
      const firstItem = data[0];
      if ('dueDate' in firstItem && 'assignedTo' in firstItem) return 'task';
      if ('firstName' in firstItem && 'lastName' in firstItem && 'score' in firstItem) return 'contact';
      if ('domain' in firstItem && 'industry' in firstItem) return 'company';
      if ('stage' in firstItem && 'value' in firstItem && 'companyName' in firstItem) return 'opportunity';
    }
    
    return 'task'; // default fallback
  },

  /**
   * Generate interactive result cards with actions
   */
  generateResultCards(
    data: any[],
    type: 'task' | 'contact' | 'company' | 'opportunity'
  ): InteractiveCard[] {
    return data.map(item => {
      switch (type) {
        case 'task':
          return this.createTaskCard(item as Task);
        case 'contact':
          return this.createContactCard(item as Lead);
        case 'company':
          return this.createCompanyCard(item as Company);
        case 'opportunity':
          return this.createOpportunityCard(item as Opportunity);
        default:
          return this.createGenericCard(item, type);
      }
    });
  },

  /**
   * Create task card
   */
  createTaskCard(task: Task): InteractiveCard {
    const dueDate = new Date(task.dueDate);
    const isOverdue = dueDate < new Date() && task.status !== 'completed';
    
    return {
      id: task.id,
      type: 'task',
      title: task.title,
      subtitle: task.description,
      priority: task.priority as 'high' | 'medium' | 'low',
      status: task.status,
      details: {
        dueDate: dueDate.toLocaleDateString(),
        priority: task.priority,
        status: task.status,
        type: task.type,
        isOverdue
      },
      actions: [
        { label: 'View Details', action: 'view', primary: true },
        ...(task.status !== 'completed' ? [{ label: 'Complete', action: 'complete' as const }] : []),
      ],
      deepLink: {
        itemType: 'task',
        itemId: task.id,
        action: 'view',
        label: task.title
      }
    };
  },

  /**
   * Create contact card
   */
  createContactCard(contact: Lead): InteractiveCard {
    return {
      id: contact.id,
      type: 'contact',
      title: `${contact.firstName} ${contact.lastName}`,
      subtitle: contact.company,
      priority: contact.score >= 80 ? 'high' : contact.score >= 60 ? 'medium' : 'low',
      status: contact.stage,
      details: {
        email: contact.email,
        phone: contact.phone,
        company: contact.company,
        score: contact.score,
        stage: contact.stage,
        source: contact.source
      },
      actions: [
        { label: 'View Contact', action: 'view', primary: true },
        { label: 'Edit', action: 'edit' }
      ],
      deepLink: {
        itemType: 'contact',
        itemId: contact.id,
        action: 'view',
        label: `${contact.firstName} ${contact.lastName}`
      }
    };
  },

  /**
   * Create company card
   */
  createCompanyCard(company: Company): InteractiveCard {
    return {
      id: company.id,
      type: 'company',
      title: company.name,
      subtitle: company.industry,
      details: {
        domain: company.domain,
        industry: company.industry,
        employeeCount: company.employeeCount,
        revenue: company.revenue,
        isCustomer: company.isCustomer,
        description: company.description
      },
      actions: [
        { label: 'View Company', action: 'view', primary: true },
        { label: 'Edit', action: 'edit' }
      ],
      deepLink: {
        itemType: 'company',
        itemId: company.id,
        action: 'view',
        label: company.name
      }
    };
  },

  /**
   * Create opportunity card
   */
  createOpportunityCard(opportunity: Opportunity): InteractiveCard {
    return {
      id: opportunity.id,
      type: 'opportunity',
      title: opportunity.name || opportunity.companyName || 'Untitled Opportunity',
      subtitle: opportunity.companyName,
      status: opportunity.stage,
      details: {
        value: opportunity.value,
        stage: opportunity.stage,
        companyName: opportunity.companyName,
        probability: opportunity.probability,
        expectedCloseDate: opportunity.expectedCloseDate
      },
      actions: [
        { label: 'View Opportunity', action: 'view', primary: true },
        { label: 'Edit', action: 'edit' }
      ],
      deepLink: {
        itemType: 'opportunity',
        itemId: opportunity.id,
        action: 'view',
        label: opportunity.name || opportunity.companyName || 'Opportunity'
      }
    };
  },

  /**
   * Create generic card fallback
   */
  createGenericCard(item: any, type: string): InteractiveCard {
    return {
      id: item.id || Math.random().toString(),
      type: type as any,
      title: item.name || item.title || 'Item',
      subtitle: item.subtitle || item.description || '',
      details: item,
      actions: [
        { label: 'View', action: 'view', primary: true }
      ],
      deepLink: {
        itemType: type as any,
        itemId: item.id,
        action: 'view',
        label: item.name || item.title || 'Item'
      }
    };
  },

  /**
   * Create deep link object for navigation
   */
  createDeepLink(
    itemType: 'task' | 'contact' | 'opportunity' | 'company',
    itemId: string,
    label: string,
    action: 'view' | 'edit' = 'view'
  ): DeepLink {
    return {
      itemType,
      itemId,
      action,
      label
    };
  },

  /**
   * Format response with suggestions
   */
  formatResponseWithSuggestions(response: string, suggestions: string[]): string {
    if (suggestions.length === 0) return response;
    
    return `${response}\n\n**You might also want to:**\n${suggestions.map(s => `• ${s}`).join('\n')}`;
  }
};
